import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Table, Badge, ListGroup, Alert } from 'react-bootstrap';
import api from '../services/api';
import { useModal } from '../context/ModalContext';
import ValidationRuleBuilder, { ValidationRule } from './ValidationRuleBuilder';
import ConditionalValueInput from './ConditionalValueInput';
import { useArea } from '../context/AreaContext';
import { buildFriendlyApiError } from '../utils/apiError';
import {
  ApproverQuestion,
  MAX_APPROVER_QUESTIONS,
  choicesToText,
  createApproverQuestion,
  isApproverQuestionFieldType,
  parseApproverQuestions,
  serializeApproverQuestions,
  textToChoices,
} from '../utils/approverQuestions';

interface CustomField {
  id?: number;
  name: string;
  label: string;
  description?: string;  // Descrição do campo exibida abaixo do título
  field_type: 'text_short' | 'text_long' | 'list' | 'combo' | 'file' | 'files' | 'date' | 'period' | 'link' | 'user' | 'analyst' | 'group' | 'integer' | 'decimal' | 'currency' | 'calculation' | 'check' | 'approver' | 'contract_manager_approver' | 'platform_document' | 'technical_proposal_review' | 'supplier_selection' | 'table';
  options?: string;
  approver_questions?: string;  // JSON com perguntas adicionais ao aprovador
  allow_multiple: boolean;
  is_required: boolean;
  order: number;
  group_name?: string;  // Nome do agrupamento (tab)
  conditional_field_id?: number;  // ID do campo que controla a visibilidade
  conditional_operator?: 'equals' | 'greater_than' | 'greater_equal' | 'less_than' | 'less_equal' | 'not_equals' | 'contains' | 'between';  // Operador de comparação
  conditional_value?: string;  // Valor(s) que ativam a exibição (JSON array, string, ou {min,max} para between)
  validation_field_id?: number;  // ID do campo usado para validação
  validation_rules?: string;  // Regras de validação em JSON
  checkbox_label?: string;  // Texto personalizado ao lado da caixa de marcação
  checkbox_text?: string;  // Texto a ser exibido quando checkbox é marcado
  checkbox_text_trigger_option?: string;  // Opção específica que deve estar marcada para exibir o texto (apenas quando allow_multiple é True)
  checkbox_link?: string;  // Link a ser exibido no campo checkbox
  checkbox_link_text?: string;  // Texto personalizado do botão do link
  document_type_id?: number;  // Tipo de documento específico para campos platform_document
}

interface StepConfig {
  id?: number;
  step_name: string;
  is_visible: boolean;
  is_required: boolean;
  is_blocked: boolean;
  order: number;
}

interface CustomFieldsManagerProps {
  show: boolean;
  onHide: () => void;
  workflowId: number;
  workflowName?: string;
  workflowVersion?: number;
  workflowParentId?: number | null;
  workflowIsPublished?: boolean;
  workflowSteps: Array<{ name: string }>;
  workflowAreaType?: 'tickets' | 'purchases' | 'changes';
}

const CustomFieldsManager: React.FC<CustomFieldsManagerProps> = ({
  show,
  onHide,
  workflowId,
  workflowName,
  workflowVersion,
  workflowParentId,
  workflowIsPublished = false,
  workflowSteps,
  workflowAreaType,
}) => {
  const readOnlyMode = workflowIsPublished;
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [showStepConfigModal, setShowStepConfigModal] = useState(false);
  const [selectedField, setSelectedField] = useState<CustomField | null>(null);
  const [stepConfigs, setStepConfigs] = useState<{ [fieldId: number]: StepConfig[] }>({});
  const { showAlert, showConfirm } = useModal();

  useEffect(() => {
    if (show && workflowId) {
      fetchFields();
    }
  }, [show, workflowId]);

  const fetchFields = async () => {
    try {
      const response = await api.get(`/api/workflows/${workflowId}/custom-fields`);
      // Ordenar campos por order
      const sortedFields = [...response.data].sort((a, b) => a.order - b.order);
      setFields(sortedFields);
      
      // Carregar configurações de step para cada campo
      const configs: { [fieldId: number]: StepConfig[] } = {};
      for (const field of response.data) {
        if (field.id) {
          const configResponse = await api.get(
            `/api/workflows/${workflowId}/custom-fields/${field.id}/step-configs`
          );
          configs[field.id] = configResponse.data;
        }
      }
      setStepConfigs(configs);
    } catch (error: any) {
      console.error('Erro ao carregar campos:', error);
      const friendly = buildFriendlyApiError(error, {
        defaultMessage: 'Não foi possível carregar os campos personalizados.',
        endpointHints: ['/custom-fields'],
      });
      showAlert({
        title: 'Erro ao carregar campos',
        message: friendly.message,
        variant: 'danger',
        details: friendly.details,
      });
    }
  };

  const handleReorder = async (fieldId: number, direction: 'up' | 'down') => {
    if (workflowIsPublished) return;
    
    const currentIndex = fields.findIndex(f => f.id === fieldId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;
    
    // Criar nova ordem
    const newFields = [...fields];
    [newFields[currentIndex], newFields[newIndex]] = [newFields[newIndex], newFields[currentIndex]];
    
    // Atualizar ordem dos campos
    const fieldOrders: { [key: number]: number } = {};
    newFields.forEach((field, index) => {
      if (field.id) {
        fieldOrders[field.id] = index;
      }
    });
    
    setLoading(true);
    try {
      await api.post(
        `/api/workflows/${workflowId}/custom-fields/reorder`,
        { field_orders: fieldOrders }
      );
      // Atualizar ordem localmente
      newFields.forEach((field, index) => {
        field.order = index;
      });
      setFields(newFields);
      showAlert({
        title: 'Sucesso',
        message: 'Ordem dos campos atualizada com sucesso!',
        variant: 'success'
      });
    } catch (error: any) {
      console.error('Erro ao reordenar campos:', error);
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao reordenar campos.',
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveField = async (field: CustomField) => {
    setLoading(true);
    try {
      if (field.id) {
        await api.put(
          `/api/workflows/${workflowId}/custom-fields/${field.id}`,
          field
        );
      } else {
        await api.post(
          `/api/workflows/${workflowId}/custom-fields`,
          { ...field, workflow_id: workflowId }
        );
      }
      await fetchFields();
      setShowFieldModal(false);
      setEditingField(null);
      showAlert({
        title: 'Sucesso',
        message: 'Campo salvo com sucesso!',
        variant: 'success'
      });
    } catch (error: any) {
      const friendly = buildFriendlyApiError(error, {
        defaultMessage: 'Não foi possível salvar o campo.',
        endpointHints: ['/custom-fields'],
      });
      showAlert({
        title: 'Erro ao Salvar',
        message: friendly.message,
        variant: 'danger',
        details: friendly.details
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteField = async (fieldId: number) => {
    const ok = await showConfirm({
      title: 'Excluir campo',
      message: 'Tem certeza que deseja excluir este campo?',
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;

    setLoading(true);
    try {
      await api.delete(`/api/workflows/${workflowId}/custom-fields/${fieldId}`);
      await fetchFields();
      showAlert({
        title: 'Sucesso',
        message: 'Campo excluído com sucesso!',
        variant: 'success'
      });
    } catch (error: any) {
      const friendly = buildFriendlyApiError(error, {
        defaultMessage: 'Não foi possível excluir o campo.',
        endpointHints: ['/custom-fields'],
      });
      showAlert({
        title: 'Erro ao Excluir',
        message: friendly.message,
        variant: 'danger',
        details: friendly.details
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStepConfig = async (fieldId: number, config: StepConfig) => {
    setLoading(true);
    try {
      let savedConfig: StepConfig;
      if (config.id) {
        const response = await api.put(
          `/api/workflows/${workflowId}/custom-fields/${fieldId}/step-configs/${config.id}`,
          config
        );
        savedConfig = response.data;
      } else {
        const response = await api.post(
          `/api/workflows/${workflowId}/custom-fields/${fieldId}/step-configs`,
          { ...config, workflow_id: workflowId, custom_field_id: fieldId }
        );
        savedConfig = response.data;
      }
      
      // Atualizar apenas a configuração específica sem recarregar tudo
      setStepConfigs(prev => {
        const fieldConfigs = prev[fieldId] || [];
        const existingIndex = fieldConfigs.findIndex(c => c.step_name === config.step_name);
        
        if (existingIndex >= 0) {
          // Atualizar configuração existente
          const updated = [...fieldConfigs];
          updated[existingIndex] = savedConfig;
          return { ...prev, [fieldId]: updated };
        } else {
          // Adicionar nova configuração
          return { ...prev, [fieldId]: [...fieldConfigs, savedConfig] };
        }
      });
      
      // Não fechar o modal - permitir continuar configurando
    } catch (error: any) {
      const friendly = buildFriendlyApiError(error, {
        defaultMessage: 'Não foi possível salvar a configuração da etapa.',
        endpointHints: ['/custom-fields', '/step-configs'],
      });
      showAlert({
        title: 'Erro ao Salvar Configuração',
        message: friendly.message,
        variant: 'danger',
        details: friendly.details
      });
    } finally {
      setLoading(false);
    }
  };

  const getFieldTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      text_short: 'Texto Curto',
      text_long: 'Texto Longo',
      list: 'Lista',
      combo: 'Combo',
      file: 'Arquivo',
      files: 'Múltiplos Arquivos',
      date: 'Data',
      period: 'Período',
      link: 'Link',
      user: 'Usuário',
      analyst: 'Usuário Analista',
      approver: 'Aprovador',
      contract_manager_approver: 'Aprovador/Gerente do Contrato',
      group: 'Grupo de Usuário',
      integer: 'Número Inteiro',
      decimal: 'Número Decimal',
      currency: 'Financeiro',
      calculation: 'Cálculo',
      technical_proposal_review: 'Análise Técnica de Proposta',
      supplier_selection: 'Fornecedores Sugeridos',
      table: 'Tabela',
    };
    return labels[type] || type;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Campos Customizados do Fluxo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {workflowIsPublished && (
            <Alert variant="warning" className="mb-3">
              <Alert.Heading>Atenção</Alert.Heading>
              <p>
                Este fluxo está publicado. A consulta da configuração está disponível em modo somente leitura.
                Para fazer alterações, crie uma nova versão.
              </p>
            </Alert>
          )}
          {!workflowIsPublished && (workflowParentId != null || (workflowVersion || 1) > 1) && (
            <Alert variant="info" className="mb-3">
              Você está editando a versão <strong>v{workflowVersion || 1}</strong>
              {workflowName ? ` do fluxo "${workflowName}"` : ''}. Chamados vinculados a outras versões publicadas
              podem não refletir imediatamente esta configuração no dashboard.
            </Alert>
          )}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6>Campos Customizados</h6>
            <Button
              size="sm"
              variant="success"
              onClick={() => {
                setEditingField({
                  name: '',
                  label: '',
                  field_type: 'text_short',
                  allow_multiple: false,
                  is_required: false,
                  order: fields.length,
                  group_name: '',
                });
                setShowFieldModal(true);
              }}
              disabled={workflowIsPublished}
            >
              + Adicionar Campo
            </Button>
          </div>

          {fields.length === 0 ? (
            <p className="text-muted text-center py-3">Nenhum campo customizado adicionado</p>
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>Ordem</th>
                  <th>Nome</th>
                  <th>Label</th>
                  <th>Agrupamento</th>
                  <th>Tipo</th>
                  <th>Obrigatório</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => (
                  <tr key={field.id}>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="p-1"
                          onClick={() => field.id && handleReorder(field.id, 'up')}
                          disabled={workflowIsPublished || index === 0 || loading}
                          title="Mover para cima"
                          style={{ minWidth: '30px', height: '25px', fontSize: '12px' }}
                        >
                          ↑
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="p-1"
                          onClick={() => field.id && handleReorder(field.id, 'down')}
                          disabled={workflowIsPublished || index === fields.length - 1 || loading}
                          title="Mover para baixo"
                          style={{ minWidth: '30px', height: '25px', fontSize: '12px' }}
                        >
                          ↓
                        </Button>
                      </div>
                    </td>
                    <td>{field.name}</td>
                    <td>{field.label}</td>
                    <td>{field.group_name || <span className="text-muted">Sem agrupamento</span>}</td>
                    <td>
                      <Badge bg="info">{getFieldTypeLabel(field.field_type)}</Badge>
                      {field.allow_multiple && <Badge bg="secondary" className="ms-1">Múltiplo</Badge>}
                    </td>
                    <td>{field.is_required ? <Badge bg="danger">Sim</Badge> : <Badge bg="secondary">Não</Badge>}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-1"
                        onClick={() => {
                          setSelectedField(field);
                          setShowStepConfigModal(true);
                        }}
                        disabled={loading}
                        title={workflowIsPublished ? "Visualizar configuração de etapas (somente leitura)" : "Configurar etapas"}
                      >
                        {workflowIsPublished ? 'Visualizar Etapas' : 'Configurar Etapas'}
                      </Button>
                      <Button
                        size="sm"
                        variant={workflowIsPublished ? 'outline-info' : 'outline-success'}
                        className="me-1"
                        onClick={() => {
                          setEditingField(field);
                          setShowFieldModal(true);
                        }}
                        disabled={loading}
                        title={workflowIsPublished ? "Visualizar campo (somente leitura)" : "Editar campo"}
                      >
                        {workflowIsPublished ? 'Visualizar' : 'Editar'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => field.id && handleDeleteField(field.id)}
                        disabled={workflowIsPublished}
                        title={workflowIsPublished ? "Não é possível excluir campos de um fluxo publicado" : "Excluir campo"}
                      >
                        Excluir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de edição/criação de campo */}
      {showFieldModal && editingField && (
        <FieldEditModal
          show={showFieldModal}
          onHide={() => {
            setShowFieldModal(false);
            setEditingField(null);
          }}
          field={editingField}
          onSave={handleSaveField}
          loading={loading}
          workflowId={workflowId}
          workflowAreaType={workflowAreaType}
          readOnly={readOnlyMode}
        />
      )}

      {/* Modal de configuração de etapas */}
      {showStepConfigModal && selectedField && (
        <StepConfigModal
          show={showStepConfigModal}
          onHide={() => {
            setShowStepConfigModal(false);
            setSelectedField(null);
          }}
          field={selectedField}
          workflowId={workflowId}
          workflowSteps={workflowSteps}
          stepConfigs={selectedField.id ? stepConfigs[selectedField.id] || [] : []}
          onSave={handleSaveStepConfig}
          loading={loading}
          readOnly={readOnlyMode}
        />
      )}
    </>
  );
};

// Modal de edição de campo
const FieldEditModal: React.FC<{
  show: boolean;
  onHide: () => void;
  field: CustomField;
  onSave: (field: CustomField) => void;
  loading: boolean;
  workflowId: number;
  workflowAreaType?: 'tickets' | 'purchases' | 'changes';
  readOnly?: boolean;
}> = ({ show, onHide, field, onSave, loading, workflowId, workflowAreaType, readOnly = false }) => {
  const { currentArea } = useArea();
  const { showAlert } = useModal();
  const [formData, setFormData] = useState<CustomField>(field);
  const [optionsText, setOptionsText] = useState(field.options || '');
  const [existingGroups, setExistingGroups] = useState<string[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [availableFields, setAvailableFields] = useState<CustomField[]>([]);
  const [validationRules, setValidationRules] = useState<ValidationRule[]>([]);
  const [documentTypes, setDocumentTypes] = useState<Array<{ id: number; name: string }>>([]);
  const [supplierLimitMode, setSupplierLimitMode] = useState<'auto' | 'limited' | 'unlimited'>('auto');
  const [supplierMaxSuppliers, setSupplierMaxSuppliers] = useState<string>('1');
  const [approverQuestions, setApproverQuestions] = useState<ApproverQuestion[]>([]);
  // Texto cru das opções por pergunta, para não perder quebras de linha durante a digitação
  const [choicesTextById, setChoicesTextById] = useState<Record<string, string>>({});
  const effectiveAreaType = String(workflowAreaType || currentArea || '').toLowerCase();
  const isPurchasesWorkflow = effectiveAreaType === 'purchases';
  const showApproverQuestions = isPurchasesWorkflow && isApproverQuestionFieldType(formData.field_type);

  useEffect(() => {
    console.log('Carregando campo para edição:', {
      id: field.id,
      conditional_field_id: field.conditional_field_id,
      conditional_operator: field.conditional_operator,
      conditional_value: field.conditional_value
    });
    
    setFormData({
      ...field,
      conditional_operator: field.conditional_operator || 'equals',
      // Garantir que conditional_value seja preservado explicitamente
      conditional_value: field.conditional_value || undefined
    });
    setOptionsText(field.options || '');
    const parsedQuestions = parseApproverQuestions(field.approver_questions);
    setApproverQuestions(parsedQuestions);
    setChoicesTextById(
      parsedQuestions.reduce<Record<string, string>>((acc, question) => {
        acc[question.id] = choicesToText(question.choices);
        return acc;
      }, {})
    );
    if (field.field_type === 'supplier_selection' && field.options) {
      try {
        const parsed = JSON.parse(field.options);
        const rawMode = String(parsed?.selection_limit_mode || '').trim().toLowerCase();
        if (rawMode === 'unlimited') {
          setSupplierLimitMode('unlimited');
          setSupplierMaxSuppliers('1');
        } else if (rawMode === 'limited') {
          const parsedMax = Number(parsed?.max_suppliers);
          setSupplierLimitMode('limited');
          setSupplierMaxSuppliers(
            Number.isInteger(parsedMax) && parsedMax > 0 ? String(parsedMax) : '1'
          );
        } else {
          setSupplierLimitMode('auto');
          setSupplierMaxSuppliers('1');
        }
      } catch {
        setSupplierLimitMode('auto');
        setSupplierMaxSuppliers('1');
      }
    } else {
      setSupplierLimitMode('auto');
      setSupplierMaxSuppliers('1');
    }
    
    // Parsear regras de validação do JSON
    if (field.validation_rules) {
      try {
        const parsed = JSON.parse(field.validation_rules);
        if (parsed.rules && Array.isArray(parsed.rules)) {
          // Converter formato antigo para novo formato se necessário
          const convertedRules: ValidationRule[] = parsed.rules.map((rule: any) => {
            if (rule.value_range && !rule.field_id) {
              // Formato antigo: converter para novo formato
              return {
                field_id: field.validation_field_id || 0,
                operator: 'greater_than',
                value: rule.value_range.min || 0,
                min_date_days: rule.min_date_days,
              };
            }
            return rule;
          });
          setValidationRules(convertedRules);
        } else {
          setValidationRules([]);
        }
      } catch {
        setValidationRules([]);
      }
    } else {
      setValidationRules([]);
    }
  }, [field]);

  // Buscar agrupamentos existentes do workflow e campos disponíveis
  useEffect(() => {
    const fetchExistingGroups = async () => {
      if (show && workflowId) {
        try {
          const response = await api.get(`/api/workflows/${workflowId}/custom-fields`);
          const fields = response.data;
          // Filtrar campos disponíveis (excluir o próprio campo se estiver editando)
          const available = fields.filter((f: CustomField) => !field.id || f.id !== field.id);
          setAvailableFields(available);
          // Extrair agrupamentos únicos e não vazios
          const groups: string[] = Array.from<string>(new Set(
            fields
              .map((f: CustomField) => f.group_name)
              .filter((name: string | undefined): name is string => !!name && name.trim() !== '')
          )).sort();
          setExistingGroups(groups);
        } catch (error) {
          console.error('Erro ao buscar agrupamentos:', error);
        }
      }
    };
    fetchExistingGroups();
  }, [show, workflowId, field.id]);

  // Buscar tipos de documento quando o campo for platform_document
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      if (show && formData.field_type === 'platform_document' && isPurchasesWorkflow) {
        try {
          const response = await api.get('/api/documents/types', {
            params: { 
              is_active: true,
            }
          });
          setDocumentTypes(response.data.map((dt: any) => ({ id: dt.id, name: dt.name })));
        } catch (error) {
          console.error('Erro ao buscar tipos de documento:', error);
        }
      } else {
        setDocumentTypes([]);
      }
    };
    fetchDocumentTypes();
  }, [show, formData.field_type, isPurchasesWorkflow]);

  // Filtrar agrupamentos conforme o usuário digita
  useEffect(() => {
    const currentValue = formData.group_name || '';
    if (currentValue.trim() === '') {
      setFilteredGroups(existingGroups);
      setShowSuggestions(false);
    } else {
      const filtered = existingGroups.filter(group =>
        group.toLowerCase().includes(currentValue.toLowerCase())
      );
      setFilteredGroups(filtered);
      setShowSuggestions(filtered.length > 0);
    }
  }, [formData.group_name, existingGroups]);

  const updateApproverQuestion = (questionId: string, changes: Partial<ApproverQuestion>) => {
    setApproverQuestions((prev) =>
      prev.map((question) => (question.id === questionId ? { ...question, ...changes } : question))
    );
  };

  const addApproverQuestion = () => {
    const question = createApproverQuestion();
    setApproverQuestions((prev) => [...prev, question]);
    setChoicesTextById((prev) => ({ ...prev, [question.id]: '' }));
  };

  const removeApproverQuestion = (questionId: string) => {
    setApproverQuestions((prev) => prev.filter((question) => question.id !== questionId));
    setChoicesTextById((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const changeApproverQuestionChoices = (questionId: string, text: string) => {
    setChoicesTextById((prev) => ({ ...prev, [questionId]: text }));
    updateApproverQuestion(questionId, { choices: textToChoices(text) });
  };

  const moveApproverQuestion = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    setApproverQuestions((prev) => {
      if (target < 0 || target >= prev.length) return prev;
      const reordered = [...prev];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return reordered;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    if (showApproverQuestions) {
      const incomplete = approverQuestions.find((question) => !question.prompt.trim());
      const withoutChoices = approverQuestions.find(
        (question) => question.answer_type === 'options' && question.choices.length === 0
      );
      const questionsError = incomplete
        ? 'Informe o texto de todas as perguntas do aprovador.'
        : withoutChoices
          ? `Informe as opções de resposta da pergunta "${withoutChoices.prompt}".`
          : null;
      if (questionsError) {
        showAlert({
          title: 'Perguntas do aprovador',
          message: questionsError,
          variant: 'warning',
        });
        return;
      }
    }
    
    // Converter regras de validação para JSON
    let validationRulesJson = undefined;
    if (validationRules.length > 0 && formData.validation_field_id) {
      validationRulesJson = JSON.stringify({ rules: validationRules });
    }
    
    // Preparar campos condicionais
    // IMPORTANTE: Para campos opcionais, precisamos enviar null explicitamente quando queremos limpar
    // porque undefined não é serializado no JSON, então o backend não sabe que queremos limpar o campo
    let conditionalFieldId: number | null | undefined = formData.conditional_field_id || null;
    let conditionalOperator: string | null | undefined = formData.conditional_operator || null;
    let conditionalValue: string | null | undefined = null;
    
    if (formData.conditional_field_id) {
      // Se há campo condicional, preservar operador e valor
      conditionalFieldId = formData.conditional_field_id;
      conditionalOperator = formData.conditional_operator || 'equals';
      conditionalValue = formData.conditional_value && formData.conditional_value.trim() !== '' 
        ? formData.conditional_value 
        : null;
    } else {
      // Se não há campo condicional, limpar tudo (enviar null explicitamente)
      conditionalFieldId = null;
      conditionalOperator = null;
      conditionalValue = null;
    }
    
    // Criar objeto para salvar - converter null para undefined para a interface TypeScript
    // mas o axios vai serializar null corretamente no JSON
    const hasCheckboxOptions = formData.field_type === 'check' && optionsText.trim() !== '';
    let supplierSelectionOptions: string | undefined = undefined;
    if (formData.field_type === 'supplier_selection') {
      if (supplierLimitMode === 'limited') {
        const parsedMax = Number(supplierMaxSuppliers);
        const safeMax = Number.isInteger(parsedMax) && parsedMax > 0 ? parsedMax : 1;
        supplierSelectionOptions = JSON.stringify({
          selection_limit_mode: 'limited',
          max_suppliers: safeMax,
        });
      } else if (supplierLimitMode === 'unlimited') {
        supplierSelectionOptions = JSON.stringify({
          selection_limit_mode: 'unlimited',
        });
      }
    }
    const fieldToSave: any = {
      ...formData,
      options:
        (formData.field_type === 'list' || formData.field_type === 'combo' || hasCheckboxOptions)
          ? optionsText
          : supplierSelectionOptions,
      // Enviar null limpa a configuração quando o campo deixa de ser de aprovador
      approver_questions: showApproverQuestions
        ? serializeApproverQuestions(approverQuestions) || null
        : null,
      validation_rules: validationRulesJson,
      // Enviar null explicitamente para que o backend saiba que queremos atualizar esses campos
      conditional_field_id: conditionalFieldId,
      conditional_operator: conditionalOperator,
      conditional_value: conditionalValue,
    };
    
    console.log('Salvando campo - dados condicionais:', {
      conditional_field_id: conditionalFieldId,
      conditional_operator: conditionalOperator,
      conditional_value: conditionalValue
    });
    
    onSave(fieldToSave);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {readOnly ? 'Visualizar Campo' : (field.id ? 'Editar Campo' : 'Novo Campo')}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
          <Form.Group className="mb-3">
            <Form.Label>Nome (identificador interno) *</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={!!field.id}
              placeholder="ex: campo_data"
            />
            <Form.Text className="text-muted">Apenas letras, números e underscore</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Label (exibição) *</Form.Label>
            <Form.Control
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              required
              placeholder="ex: Data do Evento"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrição (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value || undefined })}
              placeholder="Descrição que aparecerá abaixo do título do campo para orientar o usuário"
            />
            <Form.Text className="text-muted">
              Texto de ajuda que será exibido logo abaixo do título do campo.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tipo de Campo *</Form.Label>
            <Form.Select
              value={formData.field_type}
              onChange={(e) => setFormData({ ...formData, field_type: e.target.value as any })}
            >
              <option value="text_short">Texto Curto</option>
              <option value="text_long">Texto Longo</option>
              <option value="list">Lista</option>
              <option value="combo">Combo</option>
              <option value="file">Arquivo</option>
              <option value="files">Múltiplos Arquivos</option>
              <option value="date">Data</option>
              <option value="period">Período</option>
              <option value="link">Link</option>
              <option value="user">Usuário</option>
              <option value="analyst">Usuário Analista</option>
              <option value="approver">Aprovador</option>
              {isPurchasesWorkflow && (
                <option value="contract_manager_approver">Aprovador/Gerente do Contrato</option>
              )}
              <option value="group">Grupo de Usuário</option>
              <option value="integer">Número Inteiro</option>
              <option value="decimal">Número Decimal</option>
              <option value="currency">Financeiro (Moeda e Valor)</option>
              <option value="calculation">Cálculo</option>
              <option value="check">Checkbox</option>
              <option value="table">Tabela</option>
              {isPurchasesWorkflow && (
                <option value="platform_document">Documento da Plataforma</option>
              )}
              {isPurchasesWorkflow && (
                <option value="technical_proposal_review">Análise Técnica de Proposta</option>
              )}
              {isPurchasesWorkflow && (
                <option value="supplier_selection">Fornecedores Sugeridos</option>
              )}
            </Form.Select>
          </Form.Group>

          {formData.field_type === 'supplier_selection' && (
            <Form.Group className="mb-3">
              <Form.Label>Limite máximo de fornecedores</Form.Label>
              <Form.Select
                value={supplierLimitMode}
                onChange={(e) => setSupplierLimitMode(e.target.value as 'auto' | 'limited' | 'unlimited')}
              >
                <option value="auto">Automático pelo tipo de fluxo (compatibilidade)</option>
                <option value="limited">Definir máximo</option>
                <option value="unlimited">Sem limite máximo</option>
              </Form.Select>
              {supplierLimitMode === 'limited' && (
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  className="mt-2"
                  value={supplierMaxSuppliers}
                  onChange={(e) => setSupplierMaxSuppliers(e.target.value)}
                  placeholder="Ex: 3"
                  required
                />
              )}
              <Form.Text className="text-muted">
                Define o número máximo de fornecedores selecionáveis neste campo.
              </Form.Text>
            </Form.Group>
          )}

          {(formData.field_type === 'list' || formData.field_type === 'combo') && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Opções (uma por linha ou separadas por vírgula) *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={optionsText}
                  onChange={(e) => setOptionsText(e.target.value)}
                  placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Permitir seleção múltipla"
                  checked={formData.allow_multiple}
                  onChange={(e) => setFormData({ ...formData, allow_multiple: e.target.checked })}
                />
              </Form.Group>
            </>
          )}

          {showApproverQuestions && (
            <Form.Group className="mb-3">
              <Form.Label>Perguntas adicionais ao aprovador</Form.Label>
              <Form.Text className="text-muted d-block mb-2">
                O aprovador responderá a estas perguntas ao aprovar ou reprovar, e as respostas ficam
                registradas junto da aprovação.
              </Form.Text>

              {approverQuestions.length === 0 ? (
                <Alert variant="light" className="border py-2 mb-2">
                  <small className="text-muted">Nenhuma pergunta configurada para este aprovador.</small>
                </Alert>
              ) : (
                <ListGroup className="mb-2">
                  {approverQuestions.map((question, index) => (
                    <ListGroup.Item key={question.id}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong className="small">Pergunta {index + 1}</strong>
                        <div className="d-flex gap-1">
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            disabled={readOnly || index === 0}
                            onClick={() => moveApproverQuestion(index, 'up')}
                            title="Mover para cima"
                          >
                            ↑
                          </Button>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            disabled={readOnly || index === approverQuestions.length - 1}
                            onClick={() => moveApproverQuestion(index, 'down')}
                            title="Mover para baixo"
                          >
                            ↓
                          </Button>
                          <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            disabled={readOnly}
                            onClick={() => removeApproverQuestion(question.id)}
                          >
                            Remover
                          </Button>
                        </div>
                      </div>

                      <Form.Control
                        type="text"
                        className="mb-2"
                        value={question.prompt}
                        onChange={(e) => updateApproverQuestion(question.id, { prompt: e.target.value })}
                        placeholder="Ex: O fornecedor atende aos requisitos de compliance?"
                      />

                      <Form.Select
                        className="mb-2"
                        value={question.answer_type}
                        onChange={(e) => {
                          const answerType = e.target.value as ApproverQuestion['answer_type'];
                          updateApproverQuestion(question.id, {
                            answer_type: answerType,
                            choices: answerType === 'options' ? question.choices : [],
                            allow_multiple: answerType === 'options' ? question.allow_multiple : false,
                          });
                        }}
                      >
                        <option value="text">Resposta em texto livre</option>
                        <option value="options">Opções para o aprovador marcar</option>
                      </Form.Select>

                      {question.answer_type === 'options' && (
                        <>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            className="mb-2"
                            value={choicesTextById[question.id] ?? choicesToText(question.choices)}
                            onChange={(e) => changeApproverQuestionChoices(question.id, e.target.value)}
                            placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                          />
                          <Form.Check
                            type="checkbox"
                            label="Permitir marcar mais de uma opção"
                            checked={question.allow_multiple}
                            onChange={(e) =>
                              updateApproverQuestion(question.id, { allow_multiple: e.target.checked })
                            }
                          />
                        </>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                disabled={readOnly || approverQuestions.length >= MAX_APPROVER_QUESTIONS}
                onClick={addApproverQuestion}
              >
                Adicionar pergunta
              </Button>
            </Form.Group>
          )}

          {formData.field_type === 'platform_document' && isPurchasesWorkflow && (
            <Form.Group className="mb-3">
              <Form.Label>Tipo de Documento Específico</Form.Label>
              <Form.Select
                value={formData.document_type_id || ''}
                onChange={(e) => setFormData({ ...formData, document_type_id: e.target.value ? parseInt(e.target.value) : undefined })}
                disabled={readOnly}
              >
                <option value="">Selecione o tipo de documento</option>
                {documentTypes.map((dt) => (
                  <option key={dt.id} value={dt.id}>
                    {dt.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                O utilizador só poderá vincular documentos finalizados do tipo selecionado.
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Campo obrigatório"
              checked={formData.is_required}
              onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Agrupamento (Tab)</Form.Label>
            <div style={{ position: 'relative' }}>
              <Form.Control
                type="text"
                list={`group-name-list-${workflowId}`}
                value={formData.group_name || ''}
                onChange={(e) => {
                  setFormData({ ...formData, group_name: e.target.value || undefined });
                }}
                onFocus={() => {
                  if (formData.group_name && filteredGroups.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => {
                  // Delay para permitir clique na sugestão
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                placeholder="Ex: Informações Gerais, Detalhes Técnicos, etc."
                autoComplete="off"
              />
              <datalist id={`group-name-list-${workflowId}`}>
                {existingGroups.map((group, index) => (
                  <option key={index} value={group} />
                ))}
              </datalist>
              {showSuggestions && filteredGroups.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderTop: 'none',
                    borderRadius: '0 0 0.375rem 0.375rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  {filteredGroups.map((group, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        setFormData({ ...formData, group_name: group });
                        setShowSuggestions(false);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: index < filteredGroups.length - 1 ? '1px solid #e9ecef' : 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      {group}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Form.Text className="text-muted">
              Campos com o mesmo nome de agrupamento aparecerão na mesma tab na tela do ticket. Deixe vazio para não agrupar.
              {existingGroups.length > 0 && (
                <span className="d-block mt-1">
                  <small>Agrupamentos existentes: {existingGroups.join(', ')}</small>
                </span>
              )}
            </Form.Text>
          </Form.Group>

          {/* Campos Condicionais */}
          <Form.Group className="mb-3">
            <Form.Label>Campo Condicional (Mostrar este campo apenas se outro campo tiver um valor específico)</Form.Label>
            <Form.Select
              value={formData.conditional_field_id || ''}
              onChange={(e) => {
                const newFieldId = e.target.value ? parseInt(e.target.value) : undefined;
                const conditionalField = newFieldId ? availableFields.find(f => f.id === newFieldId) : null;
                // Determinar operador padrão baseado no tipo do campo
                let defaultOperator: 'equals' | 'greater_than' | 'greater_equal' | 'less_than' | 'less_equal' | 'not_equals' | 'contains' | 'between' = 'equals';
                if (conditionalField) {
                  if (['integer', 'decimal', 'currency'].includes(conditionalField.field_type)) {
                    defaultOperator = 'greater_equal'; // Padrão para campos numéricos
                  }
                }
                setFormData({ 
                  ...formData, 
                  conditional_field_id: newFieldId,
                  conditional_operator: newFieldId ? defaultOperator : undefined,
                  // Resetar valor condicional quando mudar o campo
                  conditional_value: newFieldId ? '' : undefined
                });
              }}
            >
              <option value="">Nenhum (sempre visível)</option>
              {availableFields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label} ({f.name})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Selecione um campo. Este campo só aparecerá quando o campo selecionado tiver o valor especificado abaixo.
            </Form.Text>
          </Form.Group>

          {formData.conditional_field_id && (() => {
            const conditionalField = availableFields.find(f => f.id === formData.conditional_field_id);
            const getOperatorsForFieldType = (fieldType?: string): Array<{ value: string; label: string }> => {
              switch (fieldType) {
                case 'text_short':
                case 'text_long':
                case 'link':
                  return [
                    { value: 'equals', label: 'Igual a' },
                    { value: 'not_equals', label: 'Diferente de' },
                    { value: 'contains', label: 'Contém' },
                  ];
                case 'integer':
                case 'decimal':
                case 'currency':
                  return [
                    { value: 'equals', label: 'Igual a' },
                    { value: 'not_equals', label: 'Diferente de' },
                    { value: 'greater_than', label: 'Maior que' },
                    { value: 'greater_equal', label: 'Maior ou igual a' },
                    { value: 'less_than', label: 'Menor que' },
                    { value: 'less_equal', label: 'Menor ou igual a' },
                    { value: 'between', label: 'Entre' },
                  ];
                case 'date':
                  return [
                    { value: 'equals', label: 'Igual a' },
                    { value: 'not_equals', label: 'Diferente de' },
                    { value: 'greater_than', label: 'Depois de' },
                    { value: 'greater_equal', label: 'Depois ou igual a' },
                    { value: 'less_than', label: 'Antes de' },
                    { value: 'less_equal', label: 'Antes ou igual a' },
                    { value: 'between', label: 'Entre' },
                  ];
                case 'list':
                case 'combo':
                  return [
                    { value: 'equals', label: 'Igual a' },
                    { value: 'not_equals', label: 'Diferente de' },
                    { value: 'contains', label: 'Contém' },
                  ];
                case 'check':
                  return [
                    { value: 'equals', label: 'Igual a' },
                    { value: 'not_equals', label: 'Diferente de' },
                  ];
                default:
                  return [
                    { value: 'equals', label: 'Igual a' },
                    { value: 'not_equals', label: 'Diferente de' },
                  ];
              }
            };

            const availableOperators = getOperatorsForFieldType(conditionalField?.field_type);

            return (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Operador de Comparação *</Form.Label>
                  <Form.Select
                    value={formData.conditional_operator || 'equals'}
                    onChange={(e) => {
                      const nextOp = e.target.value as typeof formData.conditional_operator;
                      const switchingToBetween = nextOp === 'between';
                      const switchingFromBetween = formData.conditional_operator === 'between' && !switchingToBetween;
                      setFormData({
                        ...formData,
                        conditional_operator: nextOp,
                        conditional_value: switchingToBetween || switchingFromBetween
                          ? (switchingToBetween ? '{"min":"","max":""}' : '')
                          : formData.conditional_value,
                      });
                    }}
                  >
                    {availableOperators.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Selecione como o valor do campo condicional será comparado.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Valor Condicional *</Form.Label>
                  <ConditionalValueInput
                    conditionalFieldId={formData.conditional_field_id}
                    availableFields={availableFields.filter(f => f.id !== undefined) as Array<{ id: number; label: string; name: string; field_type: string; options?: string }>}
                    value={formData.conditional_value || ''}
                    operator={formData.conditional_operator || 'equals'}
                    onChange={(value) => setFormData({ ...formData, conditional_value: value })}
                  />
                </Form.Group>
              </>
            );
          })()}

          {/* Validação Condicional */}
          <Form.Group className="mb-3">
            <Form.Label>Campo de Validação (Validar este campo baseado em outro campo)</Form.Label>
            <Form.Select
              value={formData.validation_field_id || ''}
              onChange={(e) => {
                const newFieldId = e.target.value ? parseInt(e.target.value) : undefined;
                setFormData({ 
                  ...formData, 
                  validation_field_id: newFieldId,
                  // Resetar regras de validação quando mudar o campo
                  validation_rules: newFieldId ? undefined : undefined
                });
                // Resetar regras locais
                if (!newFieldId) {
                  setValidationRules([]);
                }
              }}
            >
              <option value="">Nenhum (sem validação condicional)</option>
              {availableFields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label} ({f.name})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Selecione um campo. Este campo será validado baseado no valor do campo selecionado e nas regras especificadas abaixo.
            </Form.Text>
          </Form.Group>

          {formData.validation_field_id && (
            <Form.Group className="mb-3">
              <Form.Label>Regras de Validação *</Form.Label>
              <ValidationRuleBuilder
                availableFields={availableFields.filter(f => f.id !== undefined) as Array<{ id: number; label: string; name: string; field_type: string; options?: string }>}
                validationFieldId={formData.validation_field_id}
                rules={validationRules}
                onChange={(rules) => setValidationRules(rules)}
              />
            </Form.Group>
          )}

          {/* Configurações de Checkbox */}
          {formData.field_type === 'check' && (
            <>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Permitir marcar mais de uma opção"
                  checked={formData.allow_multiple}
                  onChange={(e) => setFormData({ ...formData, allow_multiple: e.target.checked })}
                />
                <Form.Text className="text-muted">
                  Com opções preenchidas, desmarcado = usuário escolhe apenas uma opção; marcado = usuário pode escolher várias.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Opções (uma por linha ou separadas por vírgula)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={optionsText}
                  onChange={(e) => setOptionsText(e.target.value)}
                  placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                />
                <Form.Text className="text-muted">
                  Preencha para transformar o campo em "checkbox com opções". Deixe vazio para usar checkbox simples (marcado/desmarcado).
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Texto ao Lado da Caixa de Marcação (opcional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.checkbox_label || ''}
                  onChange={(e) => setFormData({ ...formData, checkbox_label: e.target.value || undefined })}
                  placeholder="Ex: É Item de TI?"
                />
                <Form.Text className="text-muted">
                  Texto que aparecerá ao lado da caixa de marcação. Se não preenchido, será usado o label do campo.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Link do Checkbox (opcional)</Form.Label>
                <Form.Control
                  type="url"
                  value={formData.checkbox_link || ''}
                  onChange={(e) => setFormData({ ...formData, checkbox_link: e.target.value || undefined })}
                  placeholder="https://example.com/diretrizes"
                />
                <Form.Text className="text-muted">
                  Link que será exibido antes do checkbox para o usuário acessar (ex: link para diretrizes).
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Texto do Botão do Link (opcional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.checkbox_link_text || ''}
                  onChange={(e) => setFormData({ ...formData, checkbox_link_text: e.target.value || undefined })}
                  placeholder="Ex: Ler Diretrizes"
                />
                <Form.Text className="text-muted">
                  Texto que aparecerá no botão do link. Se não preenchido, será usado "Ler Diretrizes".
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Texto ao Marcar Checkbox (opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.checkbox_text || ''}
                  onChange={(e) => setFormData({ ...formData, checkbox_text: e.target.value || undefined })}
                  placeholder="Ex: A forma de devolução precisa estar registrada no MD."
                />
                <Form.Text className="text-muted">
                  {formData.allow_multiple 
                    ? "Texto que será exibido quando uma opção específica for marcada (selecione a opção abaixo)."
                    : "Texto que será exibido quando o usuário marcar o checkbox (ex: avisos ou informações importantes)."}
                </Form.Text>
              </Form.Group>

              {formData.allow_multiple && formData.checkbox_text && (
                <Form.Group className="mb-3">
                  <Form.Label>Exibir texto quando esta opção for marcada (opcional)</Form.Label>
                  <Form.Select
                    value={formData.checkbox_text_trigger_option || ''}
                    onChange={(e) => setFormData({ ...formData, checkbox_text_trigger_option: e.target.value || undefined })}
                  >
                    <option value="">Todas as opções (comportamento padrão)</option>
                    {(() => {
                      try {
                        const parsed = JSON.parse(optionsText);
                        const options = Array.isArray(parsed) ? parsed : [];
                        return options.map((opt: string) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ));
                      } catch {
                        const options = optionsText.split(/[,\n]/).map((opt: string) => opt.trim()).filter((opt: string) => opt.length > 0);
                        return options.map((opt: string) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ));
                      }
                    })()}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Selecione qual opção deve estar marcada para exibir o texto acima. Se nenhuma for selecionada, o texto será exibido quando qualquer opção for marcada.
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}
          </fieldset>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {readOnly ? 'Fechar' : 'Cancelar'}
          </Button>
          {!readOnly && (
            <Button variant="success" type="submit" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// Modal de configuração de etapas
const StepConfigModal: React.FC<{
  show: boolean;
  onHide: () => void;
  field: CustomField;
  workflowId: number;
  workflowSteps: Array<{ name: string }>;
  stepConfigs: StepConfig[];
  onSave: (fieldId: number, config: StepConfig) => void;
  loading: boolean;
  readOnly?: boolean;
}> = ({ show, onHide, field, workflowSteps, stepConfigs, onSave, loading, readOnly = false }) => {
  const [localConfigs, setLocalConfigs] = useState<StepConfig[]>(stepConfigs);

  // Atualizar configurações locais quando stepConfigs mudar
  useEffect(() => {
    setLocalConfigs(stepConfigs);
  }, [stepConfigs]);

  const getConfigForStep = (stepName: string): StepConfig | null => {
    return localConfigs.find((c) => c.step_name === stepName) || null;
  };

  const handleToggleVisibility = async (stepName: string) => {
    if (readOnly) return;
    const existing = getConfigForStep(stepName);
    const newConfig: StepConfig = existing 
      ? { ...existing, is_visible: !existing.is_visible }
      : {
          step_name: stepName,
          is_visible: true,
          is_required: !!field.is_required,
          is_blocked: false,
          order: 0,
        };
    
    // Atualizar estado local imediatamente para feedback visual
    setLocalConfigs(prev => {
      const index = prev.findIndex(c => c.step_name === stepName);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = newConfig;
        return updated;
      } else {
        return [...prev, newConfig];
      }
    });
    
    // Salvar no backend (não fecha o modal)
    await onSave(field.id!, newConfig);
  };

  const handleToggleRequired = async (stepName: string) => {
    if (readOnly) return;
    const existing = getConfigForStep(stepName);
    if (!existing) {
      // Se não existe, criar com visível=true e obrigatório=true
      const newConfig: StepConfig = {
        step_name: stepName,
        is_visible: true,
        is_required: true,
        is_blocked: false,
        order: 0,
      };
      
      setLocalConfigs(prev => [...prev, newConfig]);
      await onSave(field.id!, newConfig);
    } else {
      const newConfig: StepConfig = { ...existing, is_required: !existing.is_required };
      
      // Atualizar estado local imediatamente
      setLocalConfigs(prev => {
        const updated = [...prev];
        const index = updated.findIndex(c => c.step_name === stepName);
        if (index >= 0) {
          updated[index] = newConfig;
        }
        return updated;
      });
      
      // Salvar no backend (não fecha o modal)
      await onSave(field.id!, newConfig);
    }
  };

  const handleToggleBlocked = async (stepName: string) => {
    if (readOnly) return;
    const existing = getConfigForStep(stepName);
    if (!existing) {
      // Se não existe, criar com visível=true e bloqueado=true
      const newConfig: StepConfig = {
        step_name: stepName,
        is_visible: true,
        is_required: !!field.is_required,
        is_blocked: true,
        order: 0,
      };
      
      setLocalConfigs(prev => [...prev, newConfig]);
      await onSave(field.id!, newConfig);
    } else {
      const newConfig: StepConfig = { ...existing, is_blocked: !existing.is_blocked };
      
      // Atualizar estado local imediatamente
      setLocalConfigs(prev => {
        const updated = [...prev];
        const index = updated.findIndex(c => c.step_name === stepName);
        if (index >= 0) {
          updated[index] = newConfig;
        }
        return updated;
      });
      
      // Salvar no backend (não fecha o modal)
      await onSave(field.id!, newConfig);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Configurar Campo: {field.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
          {readOnly
            ? 'Modo consulta: visualize em quais etapas este campo é exibido, obrigatório ou bloqueado.'
            : 'Configure em quais etapas este campo será exibido, se será obrigatório e se estará bloqueado (somente leitura).'}
        </p>
        <ListGroup>
          {workflowSteps.map((step) => {
            const config = getConfigForStep(step.name);
            const isVisible = config?.is_visible ?? true;
            const isRequired = config?.is_required ?? !!field.is_required;
            const isBlocked = config?.is_blocked ?? false;

            return (
              <ListGroup.Item key={step.name}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{step.name}</strong>
                  </div>
                  <div>
                    <Form.Check
                      type="checkbox"
                      label="Visível"
                      checked={isVisible}
                      onChange={() => handleToggleVisibility(step.name)}
                      disabled={readOnly}
                      className="d-inline-block me-3"
                    />
                    <Form.Check
                      type="checkbox"
                      label="Obrigatório"
                      checked={isRequired}
                      onChange={() => handleToggleRequired(step.name)}
                      disabled={readOnly || !isVisible}
                      className="d-inline-block me-3"
                    />
                    <Form.Check
                      type="checkbox"
                      label="Bloqueado"
                      checked={isBlocked}
                      onChange={() => handleToggleBlocked(step.name)}
                      disabled={readOnly || !isVisible}
                      className="d-inline-block"
                    />
                  </div>
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomFieldsManager;

